import { api } from "@/lib/apiClient";

const CHUNK_SIZE = 5 * 1024 * 1024;
const CHUNK_THRESHOLD = 8 * 1024 * 1024;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function withRetry(fn, attempts = 3) {
  let last;
  for (let i = 0; i < attempts; i++) {
    try { return await fn(); } catch (e) {
      last = e;
      if (e?.response?.status && e.response.status < 500 && e.response.status !== 429 && e.response.status !== 408) throw e;
      await sleep(800 * (i + 1));
    }
  }
  throw last;
}

// Carica un file di un ordine. I file pesanti vengono spezzati in blocchi da 5MB
// (richieste brevi, niente timeout del proxy) e ricomposti dal server.
export async function uploadOrderFile(orderId, file, { kind = "allegato", isUpdate = false, onProgress } = {}) {
  const name = file.relPath || file.name;
  if (file.size <= CHUNK_THRESHOLD) {
    const fd = new FormData();
    fd.append("file", file, name);
    fd.append("kind", kind);
    if (isUpdate) fd.append("is_update", "true");
    return withRetry(() => api.post(`/orders/${orderId}/files`, fd, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
      onUploadProgress: (e) => onProgress?.(e.total ? e.loaded / e.total : 0),
    }));
  }
  const uploadId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const total = Math.ceil(file.size / CHUNK_SIZE);
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * CHUNK_SIZE, Math.min(file.size, (i + 1) * CHUNK_SIZE));
    const fd = new FormData();
    fd.append("chunk", blob, "part");
    fd.append("upload_id", uploadId);
    fd.append("index", String(i));
    fd.append("total", String(total));
    await withRetry(() => api.post(`/orders/${orderId}/files/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }), 4);
    onProgress?.((i + 1) / total);
  }
  const fd = new FormData();
  fd.append("upload_id", uploadId);
  fd.append("total", String(total));
  fd.append("filename", name);
  fd.append("kind", kind);
  if (isUpdate) fd.append("is_update", "true");
  return withRetry(() => api.post(`/orders/${orderId}/files/complete`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 0 }));
}

// Carica una lista di file in sequenza, con progresso complessivo (0..1) e nome file corrente
export async function uploadOrderFiles(orderId, files, opts = {}) {
  const totalBytes = files.reduce((a, f) => a + f.size, 0) || 1;
  let done = 0;
  for (const f of files) {
    await uploadOrderFile(orderId, f, { ...opts, onProgress: (p) => opts.onProgress?.((done + p * f.size) / totalBytes, f.name) });
    done += f.size;
    opts.onProgress?.(done / totalBytes, f.name);
  }
}
